import * as React from 'react';
import { FC, memo, useCallback } from 'react';

import { Badge, Box, Icon } from '@rbilabs/universal-components';

import Picture from 'components/picture';
import { RemoveRewardButton } from 'pages/loyalty/loyalty-rewards/remove-reward-button';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { LoyaltyReward } from 'state/loyalty/types';
import { primitive } from 'styles/constants';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';

import { IncentiveCardInRestaurantIncrementor } from './incentive-card-in-restaurant-incrementor';
import { IncentiveCardIncrementor } from './incentive-card-incrementor';
import {
  AddIconButton,
  AlertIcon,
  BackgroundImage,
  CardContainer,
  ImageWrapper,
  InfoContainer,
  InlineAlertContainer,
  InlineAlertSpacer,
  InlineAlertText,
  RewardText,
  StyledClickableContainer,
  StyledRewardLock,
} from './incentive-card.styled';
import { parseIncentiveData } from './parse-incentive-data';

export interface IIncentiveCard {
  incentive: LoyaltyReward;
  handlePress: ILoyaltyRewardsViewProps['handleOnPress'];
  locked?: boolean;
  inlineAlertMessage?: React.ReactNode;
  isPictureBlurhashEnabled?: boolean;
  isApplied?: boolean;
  inCooldown?: boolean;
}

export const IncentiveCard: FC<React.PropsWithChildren<IIncentiveCard>> = memo(
  ({
    incentive,
    handlePress,
    locked = false,
    inlineAlertMessage,
    isPictureBlurhashEnabled = true,
    isApplied = false,
    inCooldown = false,
  }) => {
    const rewardRedemptionLimit = useRewardRedemptionLimit();
    const appliedRewardsArray = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewardsArray);
    const incentiveData = parseIncentiveData(incentive);

    const { image, imageDescription, name, backgroundImage } = incentiveData;

    const onDisplay = useCallback(() => stopTrace(TraceName.REWARDS_LOAD), []);

    const isInRestaurant =
      isApplied &&
      appliedRewardsArray?.find(r => r.sanityId === incentive._id)?.redemptionMethod ===
        RewardRedemptionMethod.IN_RESTAURANT;

    const renderAppliedControl = () => {
      if (rewardRedemptionLimit > 1) {
        return isInRestaurant ? (
          <IncentiveCardInRestaurantIncrementor rewardId={incentive._id} />
        ) : (
          <IncentiveCardIncrementor rewardId={incentive._id} maxQuantity={rewardRedemptionLimit} />
        );
      }
      return <RemoveRewardButton rewardId={incentive._id} />;
    };

    return (
      <CardContainer>
        <StyledClickableContainer
          isApplied={isApplied}
          isLocked={locked || !!inlineAlertMessage}
          onPress={() => (!locked && !isApplied && !inCooldown ? handlePress(incentive) : null)}
        >
          {isApplied && (
            <Box position="absolute" right="$1" top="$1" zIndex={1}>
              <Badge
                variant="default-subtle"
                iconName="checkFill"
                backgroundColor={primitive.$successLight}
                _text={{ fontSize: '2xs' }}
                iconSize="3"
                paddingLeft="1"
                paddingRight="1"
              >
                {t('applied', 'Applied')}
              </Badge>
            </Box>
          )}
          <InfoContainer>
            {image ? (
              <ImageWrapper>
                {backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
                <Picture
                  isBlurhashEnabled={isPictureBlurhashEnabled}
                  image={image}
                  alt={imageDescription}
                  onDisplay={onDisplay}
                  {...incentiveCardImageDefaultProps}
                />
              </ImageWrapper>
            ) : null}
            <RewardText>{name}</RewardText>
          </InfoContainer>
          {locked ? (
            <StyledRewardLock accessibilityLabel={t('locked')} />
          ) : (
            <>
              {inlineAlertMessage ? (
                <>
                  <InlineAlertSpacer></InlineAlertSpacer>
                  <InlineAlertContainer>
                    <AlertIcon />
                    <InlineAlertText>{inlineAlertMessage}</InlineAlertText>
                  </InlineAlertContainer>
                </>
              ) : (
                <>
                  {isApplied ? (
                    renderAppliedControl()
                  ) : (
                    <AddIconButton
                      variant="outline"
                      onPress={() => handlePress(incentive)}
                      icon={<Icon variant="add" />}
                      disabled={locked}
                      testID="add-to-cart"
                      accessibilityLabel={t('addToCart')}
                    />
                  )}
                </>
              )}
            </>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
